import { useCallback, useState } from "react";
import { useApi } from "../apiCall/useApi";
import { Urls } from "../../../utils/constant";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import {
    hidePageLoader,
    showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";

export default function useDoctorSlotBlockForm() {
    const { enqueueSnackbar } = useSnackbar();
    const { apiPost } = useApi();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [doctorList, setDoctorList] = useState([]);
    const [areaList, setAreaList] = useState([]);

    // Fetch doctors for combo box
    const fetchDoctorComboList = useCallback(async () => {
        try {
            const { data } = await apiPost({
                url: Urls.getDoctorCombo(),
                data: {},
            });
            setDoctorList(data?.data || []);
        } catch (err) {
            console.log(err);
        }
    }, [apiPost]);

    // Fetch areas/locations for combo box
    const fetchAreaComboList = useCallback(async () => {
        try {
            const { data } = await apiPost({
                url: Urls.getAreaCombo(),
                data: {},
            });
            // Assuming getAreaCombo returns the direct array or { data: [...] } structure
            setAreaList(Array.isArray(data) ? data : data?.data || []);
        } catch (err) {
            console.log(err);
        }
    }, [apiPost]);

    // Fetch appointment slots
    const getAppointmentSlots = useCallback(
        async ({ doctor_id, location_id, date }) => {
            setLoading(true);
            try {
                dispatch(showPageLoader());
                const { data } = await apiPost({
                    // Make sure this URL is registered in your constant.js
                    url: Urls.getDoctorAppointmentSlots ? Urls.getDoctorAppointmentSlots() : "/appointment_slots/doctor/get",
                    data: {
                        doctor_id: doctor_id,
                        location_id: location_id,
                        date: date,
                    },
                });

                console.log("Appointment Slots payload response data:", data);

                dispatch(hidePageLoader());
                setLoading(false);
                // Return array if it's an array, or wrap the single object in an array so the frontend parser finds the slots at [0]
                return Array.isArray(data) ? data : (data?.data ? (Array.isArray(data.data) ? data.data : [data.data]) : [data]);
            } catch (err) {
                dispatch(hidePageLoader());
                console.log(err);
                setLoading(false);
                enqueueSnackbar("Error fetching appointment slots", { variant: "error" });
                return null;
            }
        },
        [apiPost, dispatch, enqueueSnackbar]
    );

    // Save selected blocked slots
    const saveBlockedSlots = useCallback(
        async ({ doctor_id, location_id, date, blocked_morning_slots, blocked_evening_slots }) => {
            setLoading(true);
            try {
                dispatch(showPageLoader());

                // Construct slots_json string from the selected slots
                const combinedSlots = [...blocked_morning_slots, ...blocked_evening_slots];
                // Creates an array of objects e.g. [{"slot_time":"09:00 AM"}]
                const slotsArray = combinedSlots.map(time => ({ slot_time: time }));

                const { data } = await apiPost({
                    // Update to match whatever your actual saving endpoint is
                    url: Urls.doctorSlotBlockAdd ? Urls.doctorSlotBlockAdd() : "/doctor_slot_block/insert_update",
                    data: {
                        doctor_id: doctor_id,
                        area_id: location_id,
                        block_date: date,
                        slots_json: JSON.stringify(slotsArray),
                        user_id: "",
                    },
                });

                enqueueSnackbar("Blocked slots successfully saved.", { variant: "success" });
                dispatch(hidePageLoader());
                setLoading(false);
                return true;
            } catch (err) {
                dispatch(hidePageLoader());
                console.log(err);
                setLoading(false);
                enqueueSnackbar("Failed to block slots.", { variant: "error" });
                return false;
            }
        },
        [apiPost, dispatch, enqueueSnackbar]
    );

    const getDoctorSlotBlockById = useCallback(
        async ({ id }) => {
            setLoading(true);
            try {
                dispatch(showPageLoader());
                const { data } = await apiPost({
                    url: Urls.doctorSlotBlockGetById ? Urls.doctorSlotBlockGetById() : "/doctor_slot_block/get_by_id",
                    data: { id },
                });

                dispatch(hidePageLoader());
                setLoading(false);
                return Array.isArray(data) ? data[0] : (data?.data ? (Array.isArray(data.data) ? data.data[0] : data.data) : data);
            } catch (err) {
                dispatch(hidePageLoader());
                console.log(err);
                setLoading(false);
                enqueueSnackbar("Error fetching slot block details", { variant: "error" });
                return null;
            }
        },
        [apiPost, dispatch, enqueueSnackbar]
    );

    return {
        doctorList,
        areaList,
        fetchDoctorComboList,
        fetchAreaComboList,
        getAppointmentSlots,
        getDoctorSlotBlockById,
        saveBlockedSlots,
        loading,
    };
}
